import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle2, AlertCircle, Building2, Users, Heart, Scale, Briefcase, FileText, LucideIcon, Lightbulb, FileCheck, ShoppingCart } from 'lucide-react';
import jsPDF from 'jspdf';

// TypeScript Interfaces
interface EntityOption {
  label: string;
  value: string;
  icon: string;
}

interface Question {
  id: string;
  question: string;
  description: string;
  options: EntityOption[];
  showIf?: (answers: Record<string, string>) => boolean;
}

interface EntityData {
  name: string;
  icon: LucideIcon;
  color: string;
  description: string;
  pros: string[];
  cons: string[];
  setupCost: string;
  setupTime: string;
  annualCosts: string;
  bestFor: string;
  requiredDocuments: string[];
  recommendedProducts: ProductRecommendation[];
}

interface ProductRecommendation {
  productId: string;
  name: string;
  price: string;
  why: string;
  priority: 'essential' | 'recommended' | 'optional';
}

interface ReasoningItem {
  factor: string;
  recommendation: string;
}

interface RecommendationResult {
  recommendations: string[];
  reasoning: ReasoningItem[];
  scores: Record<string, number>;
}

type Answers = Record<string, string>;

const BusinessStructureWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [showResult, setShowResult] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);

  // Entity type data with document and product recommendations
  const entities: Record<string, EntityData> = {
    'pty-ltd': {
      name: '(Pty) Ltd Company',
      icon: Building2,
      color: 'bg-blue-500',
      description: 'Private company with limited liability',
      pros: [
        'Limited liability protection',
        'Separate legal entity',
        'Can raise investment',
        'Professional credibility',
        '27% flat corporate tax'
      ],
      cons: [
        'Higher setup costs (R500-R1,500)',
        'Annual compliance requirements',
        'More complex administration',
        'Mandatory audits if above thresholds'
      ],
      setupCost: 'R500 - R1,500',
      setupTime: '5-21 days',
      annualCosts: 'R2,000 - R10,000',
      bestFor: 'Growth-focused businesses, multiple owners, high liability exposure',
      requiredDocuments: [
        'MOI (Memorandum of Incorporation)',
        'Shareholders Agreement (if multiple shareholders)',
        'Founders Agreement',
        'CIPC Registration Documents (CoR 14.3, CoR 15.1A)',
        'Directors Resolution Templates',
        'Share Certificates',
        'SARS Tax Registration (IT77)',
        'PAYE Registration (if hiring employees)',
        'UIF Registration',
        'Company Bank Account Mandate',
        'Service Agreements with Clients',
        'NDA Templates',
        'Employment Contracts (if hiring)',
        'Independent Contractor Agreements'
      ],
      recommendedProducts: [
        {
          productId: 'quick-start-kit',
          name: 'Stage 1: Liftoff (Quick-Start Kit)',
          price: 'R97',
          why: 'Essential foundation - includes CIPC registration guides, business structure templates, and SARS compliance checklists to get your Pty Ltd registered correctly from day one.',
          priority: 'essential'
        },
        {
          productId: 'revenue-pack',
          name: 'Stage 2: Orbit (Revenue Pack)',
          price: 'R1,997',
          why: 'Critical for client-facing operations - includes service agreements, NDAs, client onboarding documents, and POPIA compliance to protect your revenue streams.',
          priority: 'essential'
        },
        {
          productId: 'team-ops-pack',
          name: 'Stage 3: Acceleration (Team & Operations Pack)',
          price: 'R3,997',
          why: 'Needed when hiring - complete employment contract suite, contractor agreements, and HR policies to build your team legally and safely.',
          priority: 'recommended'
        },
        {
          productId: 'protection-growth-pack',
          name: 'Stage 4: Moon Landing (Protection & Growth Pack)',
          price: 'R6,997',
          why: 'For scaling and exits - shareholders agreements, buy-sell provisions, partnership agreements, and M&A templates for when you grow or seek investors.',
          priority: 'optional'
        }
      ]
    },
    'sole-proprietor': {
      name: 'Sole Proprietor',
      icon: Users,
      color: 'bg-cyan-500',
      description: 'Individual trading in own name',
      pros: [
        'Quick & easy setup',
        'Minimal costs (R0-R500)',
        'Simple tax returns',
        'Full control',
        'Low compliance burden'
      ],
      cons: [
        'Unlimited personal liability',
        'Hard to raise capital',
        'Personal tax rates (18-45%)',
        'Lower perceived credibility',
        'Cannot transfer ownership'
      ],
      setupCost: 'R0 - R500',
      setupTime: '1-2 days',
      annualCosts: 'R500 - R2,000',
      bestFor: 'Solo freelancers, consultants, service providers, testing business ideas',
      requiredDocuments: [
        'Business Name Registration (optional)',
        'SARS Income Tax Registration (IRP6)',
        'Personal Tax Number',
        'Business Bank Account (recommended)',
        'Service Agreements with Clients',
        'Invoice Templates',
        'Proposal & Quote Templates',
        'NDA Templates',
        'Independent Contractor Agreements (if outsourcing)',
        'Terms of Service (if online business)',
        'POPIA Compliance Documents (if handling personal data)'
      ],
      recommendedProducts: [
        {
          productId: 'quick-start-kit',
          name: 'Stage 1: Liftoff (Quick-Start Kit)',
          price: 'R97',
          why: 'Perfect starting point - includes sole proprietor setup guide, SARS registration steps, and basic business structure templates to launch quickly and correctly.',
          priority: 'essential'
        },
        {
          productId: 'revenue-pack',
          name: 'Stage 2: Orbit (Revenue Pack)',
          price: 'R1,997',
          why: 'Protect your client relationships - service agreements, NDAs, payment terms, and refund policies to avoid disputes and secure payments.',
          priority: 'recommended'
        }
      ]
    },
    'npc': {
      name: 'Non-Profit Company (NPC)',
      icon: Heart,
      color: 'bg-green-500',
      description: 'Company for public benefit purposes',
      pros: [
        'Tax-exempt status (Section 18A)',
        'Can receive donations',
        'Limited liability for directors',
        'Professional structure',
        'Can employ staff'
      ],
      cons: [
        'Cannot distribute profits',
        'Stricter compliance',
        'Annual financial statements',
        'Must serve public benefit'
      ],
      setupCost: 'R500 - R2,000',
      setupTime: '10-30 days',
      annualCosts: 'R3,000 - R8,000',
      bestFor: 'NGOs, charities, social enterprises, community organizations',
      requiredDocuments: [
        'MOI (Memorandum of Incorporation) for NPC',
        'CIPC Registration Documents',
        'Founding Statement',
        'Public Benefit Organisation (PBO) Application',
        'Section 18A Tax Certificate Application',
        'Director Appointment Letters',
        'Conflict of Interest Policy',
        'Donation Receipt Templates',
        'Grant Application Templates',
        'Employment Contracts for Staff',
        'Volunteer Agreements',
        'Financial Policies & Procedures',
        'Annual Report Template',
        'Beneficiary Agreements'
      ],
      recommendedProducts: [
        {
          productId: 'quick-start-kit',
          name: 'Stage 1: Liftoff (Quick-Start Kit)',
          price: 'R97',
          why: 'Foundation setup - CIPC registration for NPCs, compliance basics, and governance structures tailored for non-profit organizations.',
          priority: 'essential'
        },
        {
          productId: 'team-ops-pack',
          name: 'Stage 3: Acceleration (Team & Operations Pack)',
          price: 'R3,997',
          why: 'For growing NPCs with staff - employment contracts, volunteer agreements, and operational policies to manage your team legally.',
          priority: 'recommended'
        }
      ]
    },
    'trust': {
      name: 'Trust',
      icon: Scale,
      color: 'bg-purple-500',
      description: 'Legal arrangement where trustees hold assets',
      pros: [
        'Asset protection',
        'Estate planning benefits',
        'Flexible structure',
        'Can hold multiple assets',
        'Perpetual succession'
      ],
      cons: [
        'Complex to set up',
        'Requires trust deed',
        'Trustees have fiduciary duties',
        'Tax implications on distributions',
        'Cannot conduct certain businesses'
      ],
      setupCost: 'R3,000 - R15,000',
      setupTime: '14-30 days',
      annualCosts: 'R5,000 - R20,000',
      bestFor: 'Family wealth, property holding, asset protection, succession planning',
      requiredDocuments: [
        'Trust Deed (professionally drafted)',
        'Master of High Court Registration',
        'Letter of Authority',
        'Trustees Appointment Letters',
        'Trust Bank Account Mandate',
        'IT77 Tax Registration (Trust)',
        'Annual Financial Statements',
        'Trustees Resolution Templates',
        'Beneficiary Distribution Records',
        'Asset Transfer Agreements',
        'Loan Agreements (if applicable)',
        'Property Lease Agreements (if holding property)',
        'Investment Policy Statement',
        'Succession Planning Documents'
      ],
      recommendedProducts: [
        {
          productId: 'quick-start-kit',
          name: 'Stage 1: Liftoff (Quick-Start Kit)',
          price: 'R97',
          why: 'Understand trust basics - structure overview and compliance requirements, though you\'ll need professional help for the trust deed itself.',
          priority: 'recommended'
        },
        {
          productId: 'protection-growth-pack',
          name: 'Stage 4: Moon Landing (Protection & Growth Pack)',
          price: 'R6,997',
          why: 'Advanced asset protection - succession planning, buy-sell agreements, and dispute resolution for complex trust structures.',
          priority: 'recommended'
        }
      ]
    },
    'partnership': {
      name: 'Partnership',
      icon: Briefcase,
      color: 'bg-orange-500',
      description: 'Agreement between 2+ persons for profit',
      pros: [
        'Simple to establish',
        'Shared resources & expertise',
        'Flexible profit sharing',
        'Lower setup costs',
        'Pass-through taxation'
      ],
      cons: [
        'Unlimited joint liability',
        'Partners liable for each other',
        'Disputes can be complex',
        'No separate legal personality',
        'Difficult to transfer interest'
      ],
      setupCost: 'R1,000 - R5,000',
      setupTime: '3-7 days',
      annualCosts: 'R2,000 - R8,000',
      bestFor: 'Professional services, joint ventures, collaborative businesses',
      requiredDocuments: [
        'Partnership Agreement (CRITICAL)',
        'SARS Partnership Registration',
        'Partner Contribution Agreements',
        'Profit Sharing Agreement',
        'Decision-Making Framework',
        'Dispute Resolution Procedures',
        'Exit & Buyout Clauses',
        'Service Agreements with Clients',
        'NDA Templates',
        'Independent Contractor Agreements',
        'Partnership Bank Account Mandate',
        'Loan Agreements (if partners lend to partnership)',
        'Property/IP Ownership Agreements',
        'Partner Non-Compete Clauses'
      ],
      recommendedProducts: [
        {
          productId: 'quick-start-kit',
          name: 'Stage 1: Liftoff (Quick-Start Kit)',
          price: 'R97',
          why: 'Essential foundation - includes basic partnership agreement templates and SARS registration guides to formalize your partnership correctly.',
          priority: 'essential'
        },
        {
          productId: 'revenue-pack',
          name: 'Stage 2: Orbit (Revenue Pack)',
          price: 'R1,997',
          why: 'Client-facing documents - service agreements and NDAs to protect the partnership when dealing with customers and third parties.',
          priority: 'recommended'
        },
        {
          productId: 'protection-growth-pack',
          name: 'Stage 4: Moon Landing (Protection & Growth Pack)',
          price: 'R6,997',
          why: 'Advanced partnership protection - comprehensive partnership agreements, dispute resolution, and exit mechanisms to prevent costly conflicts.',
          priority: 'recommended'
        }
      ]
    },
    'inc': {
      name: 'Incorporated Professional Practice',
      icon: FileText,
      color: 'bg-indigo-500',
      description: 'Professional practice with incorporation',
      pros: [
        'Limited liability (with exceptions)',
        'Professional credibility',
        'Can have multiple directors',
        'Perpetual succession',
        'Structured governance'
      ],
      cons: [
        'Subject to professional rules',
        'Personal liability for malpractice',
        'Higher compliance costs',
        'Professional body oversight',
        'Restricted ownership'
      ],
      setupCost: 'R2,000 - R8,000',
      setupTime: '14-45 days',
      annualCosts: 'R5,000 - R15,000',
      bestFor: 'Lawyers, accountants, doctors, architects, engineers',
      requiredDocuments: [
        'MOI (Memorandum of Incorporation)',
        'Professional Body Approval',
        'CIPC Registration Documents',
        'Professional Indemnity Insurance',
        'Directors Service Agreements',
        'Shareholders Agreement',
        'Client Engagement Letters',
        'Terms of Business',
        'NDA Templates',
        'Professional Services Agreements',
        'Conflict of Interest Policy',
        'Quality Assurance Procedures',
        'Client Complaints Procedure',
        'Professional Liability Waivers'
      ],
      recommendedProducts: [
        {
          productId: 'quick-start-kit',
          name: 'Stage 1: Liftoff (Quick-Start Kit)',
          price: 'R97',
          why: 'Professional practice foundation - CIPC registration for Inc., professional body compliance, and incorporation basics.',
          priority: 'essential'
        },
        {
          productId: 'revenue-pack',
          name: 'Stage 2: Orbit (Revenue Pack)',
          price: 'R1,997',
          why: 'Essential for client work - professional services agreements, engagement letters, and liability protection documents.',
          priority: 'essential'
        },
        {
          productId: 'team-ops-pack',
          name: 'Stage 3: Acceleration (Team & Operations Pack)',
          price: 'R3,997',
          why: 'For growing practices - employment contracts for associates, partnership track agreements, and operational policies.',
          priority: 'recommended'
        }
      ]
    }
  };

  // Decision tree questions (same as before)
  const questions: Question[] = [
    {
      id: 'profit-motive',
      question: 'What is your primary purpose?',
      description: 'This helps determine if you need a for-profit or non-profit entity',
      options: [
        { label: 'Generate profit for owners/shareholders', value: 'profit', icon: '💰' },
        { label: 'Serve public benefit or charitable purpose (no profit distribution)', value: 'non-profit', icon: '❤️' }
      ]
    },
    {
      id: 'regulated-profession',
      question: 'Are you a regulated professional?',
      description: 'Certain professions have specific incorporation requirements under professional body rules',
      options: [
        { label: 'Yes - lawyer, doctor, accountant, engineer, architect', value: 'yes', icon: '⚖️' },
        { label: 'No - general business or service provider', value: 'no', icon: '💼' }
      ],
      showIf: (answers) => answers['profit-motive'] === 'profit'
    },
    {
      id: 'asset-holding-primary',
      question: 'Is your PRIMARY activity holding assets (not trading)?',
      description: 'Trusts are designed for passive asset holding, not active business operations',
      options: [
        { label: 'Yes - holding property, investments, or family wealth', value: 'yes', icon: '🏛️' },
        { label: 'No - providing services, selling products, or active trading', value: 'no', icon: '🚀' }
      ],
      showIf: (answers) => answers['profit-motive'] === 'profit' && answers['regulated-profession'] === 'no'
    },
    {
      id: 'multiple-owners',
      question: 'How many beneficial owners will there be initially?',
      description: 'Consider co-founders, partners, or shareholders (not employees)',
      options: [
        { label: 'Just me (sole owner)', value: 'single', icon: '👤' },
        { label: '2 people', value: 'two', icon: '👥' },
        { label: '3 or more people', value: 'multiple', icon: '👨‍👩‍👧‍👦' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'partner-relationship',
      question: 'What is the relationship between owners?',
      description: 'This affects the recommended governance structure',
      options: [
        { label: 'Business partners (equal or near-equal stakes)', value: 'equal-partners', icon: '🤝' },
        { label: 'Family members or close friends', value: 'family', icon: '👨‍👩‍👧' },
        { label: 'Investor(s) + active founder(s)', value: 'investor-founder', icon: '💼' }
      ],
      showIf: (answers) => ['two', 'multiple'].includes(answers['multiple-owners'])
    },
    {
      id: 'liability-exposure',
      question: 'What is your liability exposure level?',
      description: 'Consider contracts, potential lawsuits, product liability, or professional negligence',
      options: [
        { label: 'High - significant contracts, products, or professional services', value: 'high', icon: '⚠️' },
        { label: 'Medium - some client contracts or modest operational risk', value: 'medium', icon: '⚡' },
        { label: 'Low - minimal risk, testing idea, or very small scale', value: 'low', icon: '✨' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'expected-revenue',
      question: 'What is your expected annual revenue within 12 months?',
      description: 'Be realistic - higher revenue justifies incorporation costs',
      options: [
        { label: 'Under R250k (part-time or side hustle)', value: 'very-low', icon: '📊' },
        { label: 'R250k - R500k (modest full-time income)', value: 'low', icon: '📈' },
        { label: 'R500k - R1M (established small business)', value: 'medium', icon: '💼' },
        { label: 'R1M - R5M (growing business)', value: 'high', icon: '🚀' },
        { label: 'Over R5M (significant operation)', value: 'very-high', icon: '🏢' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'growth-plans',
      question: 'Do you plan to raise external investment or grow significantly?',
      description: 'Investors typically require a company structure, not sole proprietorship',
      options: [
        { label: 'Yes - plan to raise funding or scale rapidly', value: 'yes', icon: '📈' },
        { label: 'Maybe - open to it but not immediately', value: 'maybe', icon: '🤔' },
        { label: 'No - lifestyle business or service provider', value: 'no', icon: '🌱' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'employees-planned',
      question: 'Do you plan to hire employees (not contractors)?',
      description: 'Employment contracts and PAYE obligations affect entity choice',
      options: [
        { label: 'Yes - will hire employees within 12 months', value: 'yes', icon: '👥' },
        { label: 'Maybe - might hire later', value: 'maybe', icon: '🤷' },
        { label: 'No - solo or contractors only', value: 'no', icon: '👤' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'client-type',
      question: 'Who are your primary clients/customers?',
      description: 'Corporate clients often prefer dealing with registered companies',
      options: [
        { label: 'Large corporates or government', value: 'corporate', icon: '🏢' },
        { label: 'SMEs or small businesses', value: 'sme', icon: '💼' },
        { label: 'Individual consumers (B2C)', value: 'consumer', icon: '👨‍👩‍👧‍👦' },
        { label: 'Mix of above', value: 'mixed', icon: '🎯' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'setup-urgency',
      question: 'How quickly do you need to start operating?',
      description: 'Company registration takes longer than sole proprietor setup',
      options: [
        { label: 'Immediately (within 1 week)', value: 'immediate', icon: '⚡' },
        { label: 'Within 1 month', value: 'moderate', icon: '📅' },
        { label: 'Can wait 1-2 months for proper setup', value: 'patient', icon: '⏳' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'admin-capability',
      question: 'What is your capacity for ongoing administration and compliance?',
      description: 'Companies require annual returns, financial statements, and more complex bookkeeping',
      options: [
        { label: 'High - have accounting support or can manage complexity', value: 'high', icon: '📋' },
        { label: 'Medium - can handle moderate admin with occasional help', value: 'medium', icon: '📝' },
        { label: 'Low - want minimal paperwork and compliance', value: 'low', icon: '🎯' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    },
    {
      id: 'exit-plans',
      question: 'Do you plan to sell the business in the future?',
      description: 'Companies are much easier to sell than sole proprietorships',
      options: [
        { label: 'Yes - plan to build and sell', value: 'yes', icon: '💰' },
        { label: 'Maybe - want to keep options open', value: 'maybe', icon: '🤔' },
        { label: 'No - lifestyle business for long term', value: 'no', icon: '🌳' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no' &&
               answers['multiple-owners'] === 'single';
      }
    },
    {
      id: 'existing-assets',
      question: 'Do you have significant existing business assets to protect?',
      description: 'Equipment, IP, property, or valuable contracts that need protection',
      options: [
        { label: 'Yes - substantial assets or IP', value: 'yes', icon: '💎' },
        { label: 'Some - modest assets', value: 'some', icon: '📦' },
        { label: 'No - starting from scratch', value: 'no', icon: '🆕' }
      ],
      showIf: (answers) => {
        return answers['profit-motive'] === 'profit' &&
               answers['regulated-profession'] === 'no' &&
               answers['asset-holding-primary'] === 'no';
      }
    }
  ];

  // Recommendation logic (keeping the same scoring algorithm)
  const getRecommendations = (): RecommendationResult => {
    const scores: Record<string, number> = {
      'pty-ltd': 0,
      'sole-proprietor': 0,
      'npc': 0,
      'trust': 0,
      'partnership': 0,
      'inc': 0
    };

    const reasoning: ReasoningItem[] = [];

    // Non-profit path
    if (answers['profit-motive'] === 'non-profit') {
      scores['npc'] = 100;
      reasoning.push({
        factor: 'Non-profit purpose',
        recommendation: 'NPC is the only appropriate structure for organizations serving public benefit without profit distribution.'
      });
      scores['pty-ltd'] = 30;
      reasoning.push({
        factor: 'Alternative consideration',
        recommendation: 'If you need some commercial revenue alongside social impact, a Pty Ltd with B-BBEE or social enterprise focus might work.'
      });
    }

    // Regulated profession path
    if (answers['regulated-profession'] === 'yes') {
      scores['inc'] = 90;
      scores['pty-ltd'] = 75;
      reasoning.push({
        factor: 'Regulated profession',
        recommendation: 'Incorporated Professional Practice (Inc.) is designed for your profession, but a standard Pty Ltd may also work depending on professional body rules.'
      });
    }

    // Asset holding primary activity
    if (answers['asset-holding-primary'] === 'yes') {
      scores['trust'] = 85;
      scores['pty-ltd'] = 60;
      reasoning.push({
        factor: 'Asset holding focus',
        recommendation: 'If passive asset holding and estate planning are priorities, a Trust is appropriate. However, if there\'s any active trading, a Pty Ltd holding company is better.'
      });
    }

    // Multiple owners scoring
    if (answers['multiple-owners'] === 'two') {
      scores['pty-ltd'] += 40;
      scores['partnership'] += 30;
      reasoning.push({
        factor: 'Two owners',
        recommendation: 'A Pty Ltd provides better liability protection and growth options. A Partnership is simpler but carries joint liability risks.'
      });
    } else if (answers['multiple-owners'] === 'multiple') {
      scores['pty-ltd'] += 50;
      scores['partnership'] += 20;
      reasoning.push({
        factor: 'Multiple owners',
        recommendation: 'With 3+ owners, a Pty Ltd offers clearer governance, limited liability, and easier dispute resolution mechanisms.'
      });
    } else if (answers['multiple-owners'] === 'single') {
      scores['sole-proprietor'] += 30;
      scores['pty-ltd'] += 25;
      reasoning.push({
        factor: 'Single owner',
        recommendation: 'As a solo founder, you have flexibility. Choose based on liability exposure, revenue scale, and growth ambitions.'
      });
    }

    // Partner relationship
    if (answers['partner-relationship'] === 'equal-partners') {
      scores['pty-ltd'] += 35;
      scores['partnership'] += 20;
      reasoning.push({
        factor: 'Equal partnership',
        recommendation: 'Equal partners benefit from a Pty Ltd\'s formal shareholder agreements with dispute resolution clauses.'
      });
    } else if (answers['partner-relationship'] === 'investor-founder') {
      scores['pty-ltd'] += 50;
      scores['partnership'] = Math.max(0, scores['partnership'] - 30);
      reasoning.push({
        factor: 'Investor involvement',
        recommendation: 'Investors require a Pty Ltd structure for proper equity, governance, and exit mechanisms.'
      });
    }

    // Liability exposure
    if (answers['liability-exposure'] === 'high') {
      scores['pty-ltd'] += 50;
      scores['inc'] += 40;
      scores['sole-proprietor'] = Math.max(0, scores['sole-proprietor'] - 40);
      scores['partnership'] = Math.max(0, scores['partnership'] - 40);
      reasoning.push({
        factor: 'High liability exposure',
        recommendation: 'Your liability risk makes limited liability protection essential. A Pty Ltd or Inc. protects personal assets from business liabilities.'
      });
    } else if (answers['liability-exposure'] === 'medium') {
      scores['pty-ltd'] += 30;
      scores['sole-proprietor'] += 5;
      reasoning.push({
        factor: 'Moderate liability',
        recommendation: 'With moderate risk, a Pty Ltd is safer, but adequate insurance might allow sole proprietorship for simplicity.'
      });
    } else if (answers['liability-exposure'] === 'low') {
      scores['sole-proprietor'] += 25;
      scores['pty-ltd'] += 10;
      reasoning.push({
        factor: 'Low liability risk',
        recommendation: 'Low risk allows flexibility. Consider starting as sole proprietor and incorporating later as you grow.'
      });
    }

    // Revenue expectations
    if (answers['expected-revenue'] === 'very-high') {
      scores['pty-ltd'] += 60;
      scores['sole-proprietor'] = Math.max(0, scores['sole-proprietor'] - 30);
      reasoning.push({
        factor: 'High revenue (R5M+)',
        recommendation: 'At this scale, a Pty Ltd is essential for tax efficiency (27% vs 45%), credibility, and operational structure.'
      });
    } else if (answers['expected-revenue'] === 'high') {
      scores['pty-ltd'] += 45;
      scores['sole-proprietor'] = Math.max(0, scores['sole-proprietor'] - 20);
      reasoning.push({
        factor: 'Strong revenue (R1M-R5M)',
        recommendation: 'This revenue level strongly favors Pty Ltd for tax optimization and professional infrastructure.'
      });
    } else if (answers['expected-revenue'] === 'medium') {
      scores['pty-ltd'] += 30;
      scores['sole-proprietor'] += 15;
      reasoning.push({
        factor: 'Medium revenue (R500k-R1M)',
        recommendation: 'At this level, both work. Pty Ltd if growth-focused; sole proprietor if lifestyle business with minimal complexity.'
      });
    } else if (answers['expected-revenue'] === 'low') {
      scores['sole-proprietor'] += 30;
      scores['pty-ltd'] += 15;
      reasoning.push({
        factor: 'Modest revenue (R250k-R500k)',
        recommendation: 'Sole proprietorship keeps costs low initially. Incorporate when revenue or complexity increases.'
      });
    } else if (answers['expected-revenue'] === 'very-low') {
      scores['sole-proprietor'] += 40;
      scores['pty-ltd'] += 5;
      reasoning.push({
        factor: 'Low revenue (under R250k)',
        recommendation: 'Start as sole proprietor to minimize setup costs and test your concept before incorporating.'
      });
    }

    // Growth plans
    if (answers['growth-plans'] === 'yes') {
      scores['pty-ltd'] += 45;
      scores['sole-proprietor'] = Math.max(0, scores['sole-proprietor'] - 25);
      reasoning.push({
        factor: 'Investment & growth plans',
        recommendation: 'If you plan to raise funding, start with Pty Ltd. Investors require proper equity structures from day one.'
      });
    } else if (answers['growth-plans'] === 'maybe') {
      scores['pty-ltd'] += 25;
      reasoning.push({
        factor: 'Possible future growth',
        recommendation: 'Consider Pty Ltd to avoid costly conversion later if opportunities arise.'
      });
    }

    // Employee plans
    if (answers['employees-planned'] === 'yes') {
      scores['pty-ltd'] += 30;
      reasoning.push({
        factor: 'Hiring employees',
        recommendation: 'Employing staff is easier through a Pty Ltd with proper employment contracts and PAYE structure.'
      });
    }

    // Client type
    if (answers['client-type'] === 'corporate') {
      scores['pty-ltd'] += 35;
      scores['sole-proprietor'] = Math.max(0, scores['sole-proprietor'] - 20);
      reasoning.push({
        factor: 'Corporate clients',
        recommendation: 'Large clients and government often require supplier registration as a registered company (Pty Ltd).'
      });
    } else if (answers['client-type'] === 'consumer') {
      scores['sole-proprietor'] += 15;
      reasoning.push({
        factor: 'Consumer clients',
        recommendation: 'B2C businesses have more flexibility - structure matters less to individual customers.'
      });
    }

    // Setup urgency
    if (answers['setup-urgency'] === 'immediate') {
      scores['sole-proprietor'] += 25;
      reasoning.push({
        factor: 'Immediate start needed',
        recommendation: 'If you need to start immediately, sole proprietor lets you operate within days. You can incorporate later.'
      });
    } else if (answers['setup-urgency'] === 'patient') {
      scores['pty-ltd'] += 15;
      reasoning.push({
        factor: 'Time for proper setup',
        recommendation: 'With time to plan, invest in proper Pty Ltd setup to avoid conversion costs later.'
      });
    }

    // Admin capability
    if (answers['admin-capability'] === 'low') {
      scores['sole-proprietor'] += 25;
      scores['pty-ltd'] = Math.max(0, scores['pty-ltd'] - 15);
      reasoning.push({
        factor: 'Limited admin capacity',
        recommendation: 'If compliance overwhelms you, sole proprietor has minimal paperwork. Budget for bookkeeper if choosing Pty Ltd.'
      });
    } else if (answers['admin-capability'] === 'high') {
      scores['pty-ltd'] += 20;
      reasoning.push({
        factor: 'Strong admin capability',
        recommendation: 'With accounting support, Pty Ltd compliance is manageable and provides better long-term structure.'
      });
    }

    // Exit plans
    if (answers['exit-plans'] === 'yes') {
      scores['pty-ltd'] += 40;
      scores['sole-proprietor'] = Math.max(0, scores['sole-proprietor'] - 30);
      reasoning.push({
        factor: 'Planning to sell',
        recommendation: 'If you plan to sell the business, build it as a Pty Ltd from day one. Sole proprietorships can only sell assets, not the business.'
      });
    }

    // Existing assets
    if (answers['existing-assets'] === 'yes') {
      scores['pty-ltd'] += 30;
      scores['trust'] += 20;
      reasoning.push({
        factor: 'Significant existing assets',
        recommendation: 'Protect valuable assets with limited liability structure (Pty Ltd) or separate holding vehicle (Trust).'
      });
    }

    // BIAS TOWARD PTY LTD
    if (scores['pty-ltd'] > 50 && scores['pty-ltd'] >= scores['sole-proprietor'] - 10) {
      scores['pty-ltd'] += 15;
      reasoning.push({
        factor: 'Future-proofing',
        recommendation: 'When in doubt between structures, Pty Ltd provides more flexibility for growth and changes.'
      });
    }

    // Sort entities by score
    const sortedEntities = Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .filter(([, score]) => score > 20)
      .slice(0, 3);

    // Ensure at least 2 recommendations
    if (sortedEntities.length === 1) {
      if (sortedEntities[0][0] !== 'pty-ltd') {
        sortedEntities.push(['pty-ltd', 50]);
      } else {
        sortedEntities.push(['sole-proprietor', 40]);
      }
    }

    return {
      recommendations: sortedEntities.map(([id]) => id),
      reasoning: reasoning.slice(0, 5),
      scores
    };
  };

  // PDF Generation Function
  const generatePDF = () => {
    setIsGeneratingPDF(true);

    try {
      const { recommendations, reasoning } = getRecommendations();
      const primaryId = recommendations[0];
      const primary = entities[primaryId];
      const alternatives = recommendations.slice(1).map(id => entities[id]);

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPos = 20;

      // Helper function to check if we need a new page
      const checkPageBreak = (requiredSpace: number) => {
        if (yPos + requiredSpace > pdf.internal.pageSize.getHeight() - 20) {
          pdf.addPage();
          yPos = 20;
          return true;
        }
        return false;
      };

      // Header
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Business Structure Recommendation Report', margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-ZA')}`, margin, yPos);
      pdf.text('Deal Velocity - Business Structure Wizard', pageWidth - margin, yPos, { align: 'right' });
      yPos += 10;

      // Primary Recommendation
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Primary Recommendation', margin, yPos);
      yPos += 6;

      pdf.setFontSize(12);
      pdf.setTextColor(37, 99, 235);
      pdf.text(primary.name, margin, yPos);
      yPos += 5;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60);
      pdf.text(primary.description, margin, yPos);
      yPos += 6;

      // Quick Stats
      checkPageBreak(18);
      pdf.setFillColor(240, 240, 245);
      pdf.rect(margin, yPos, contentWidth, 16, 'F');
      pdf.setFontSize(8);
      pdf.setTextColor(0);
      pdf.text(`Setup Cost: ${primary.setupCost}`, margin + 4, yPos + 5);
      pdf.text(`Setup Time: ${primary.setupTime}`, margin + 4, yPos + 11);
      pdf.text(`Annual Costs: ${primary.annualCosts}`, margin + contentWidth/2, yPos + 5);
      pdf.text(`Best For: ${primary.bestFor}`, margin + contentWidth/2, yPos + 11);
      yPos += 19;

      // Key Decision Factors
      checkPageBreak(25);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0);
      pdf.text('Key Decision Factors', margin, yPos);
      yPos += 6;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      reasoning.forEach((item, idx) => {
        checkPageBreak(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${idx + 1}. ${item.factor}`, margin, yPos);
        yPos += 4;
        pdf.setFont('helvetica', 'normal');
        const lines = pdf.splitTextToSize(item.recommendation, contentWidth - 10);
        pdf.text(lines, margin + 5, yPos);
        yPos += lines.length * 3.5 + 2;
      });

      yPos += 3;

      // Advantages
      checkPageBreak(20);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(34, 197, 94);
      pdf.text('Advantages', margin, yPos);
      yPos += 5;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0);
      primary.pros.forEach((pro) => {
        checkPageBreak(6);
        pdf.text(`✓ ${pro}`, margin + 5, yPos);
        yPos += 4;
      });

      yPos += 3;

      // Considerations
      checkPageBreak(20);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(234, 88, 12);
      pdf.text('Considerations', margin, yPos);
      yPos += 5;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(0);
      primary.cons.forEach((con) => {
        checkPageBreak(6);
        pdf.text(`! ${con}`, margin + 5, yPos);
        yPos += 4;
      });

      yPos += 5;

      // Required Documents - 2 column layout
      checkPageBreak(35);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0);
      pdf.text('Required Documents & Agreements', margin, yPos);
      yPos += 6;

      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      const columnWidth = contentWidth / 2 - 5;
      let docYPos = yPos;
      primary.requiredDocuments.forEach((doc, idx) => {
        const isLeftColumn = idx % 2 === 0;
        const xPos = isLeftColumn ? margin + 5 : margin + contentWidth/2 + 5;

        if (!isLeftColumn && idx > 0) {
          // Right column - don't increment yPos yet
        } else if (idx > 0 && idx % 2 === 0) {
          // New row
          docYPos += 4;
          checkPageBreak(6);
        }

        const docText = pdf.splitTextToSize(`${idx + 1}. ${doc}`, columnWidth);
        pdf.text(docText, xPos, docYPos);

        if (!isLeftColumn) {
          docYPos += docText.length * 4;
        }
      });
      yPos = docYPos + 5;

      // Recommended Products
      checkPageBreak(30);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Recommended Deal Velocity Products', margin, yPos);
      yPos += 6;

      primary.recommendedProducts.forEach((product) => {
        checkPageBreak(18);

        // Product priority badge
        const priorityColors: Record<string, [number, number, number]> = {
          essential: [220, 38, 38],
          recommended: [37, 99, 235],
          optional: [100, 116, 139]
        };
        const [r, g, b] = priorityColors[product.priority];
        pdf.setFillColor(r, g, b);
        pdf.rect(margin, yPos - 2, 1.5, 4, 'F');

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0);
        pdf.text(`${product.name} - ${product.price}`, margin + 4, yPos);
        yPos += 4;

        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60);
        const whyLines = pdf.splitTextToSize(product.why, contentWidth - 10);
        pdf.text(whyLines, margin + 4, yPos);
        yPos += whyLines.length * 3 + 4;
      });

      // Alternative Options
      if (alternatives.length > 0) {
        checkPageBreak(30);

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0);
        pdf.text('Alternative Options', margin, yPos);
        yPos += 6;

        alternatives.forEach((alt) => {
          checkPageBreak(35);

          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(alt.name, margin, yPos);
          yPos += 4;

          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(60);
          const descLines = pdf.splitTextToSize(alt.description, contentWidth);
          pdf.text(descLines, margin, yPos);
          yPos += descLines.length * 3.5 + 2;

          pdf.setTextColor(0);
          pdf.setFontSize(7);
          pdf.text(`Setup: ${alt.setupCost} | Time: ${alt.setupTime} | Annual: ${alt.annualCosts}`, margin, yPos);
          yPos += 4;

          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'italic');
          pdf.text('Best for:', margin, yPos);
          yPos += 3;
          const bestForLines = pdf.splitTextToSize(alt.bestFor, contentWidth - 8);
          pdf.setFont('helvetica', 'normal');
          pdf.text(bestForLines, margin + 3, yPos);
          yPos += bestForLines.length * 3 + 5;
        });
      }

      // Disclaimer
      checkPageBreak(50);
      yPos += 5;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(180, 83, 9);
      pdf.text('Important Legal Disclaimer', margin, yPos);
      yPos += 5;

      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60);

      const disclaimerText = [
        'This recommendation tool provides general information only and should not be considered professional legal, tax, or financial advice. Business structure decisions are complex and depend on your specific circumstances, which this tool cannot fully assess.',
        '',
        'While we endeavor to keep information accurate and up-to-date, we make no representations or warranties about the completeness, accuracy, or suitability of the recommendations for your specific situation. Laws and regulations change frequently and their application varies based on individual circumstances.',
        '',
        'Before making any final decision, you should consult with qualified professional advisors including an attorney, accountant, and/or tax specialist who can evaluate your specific situation and provide personalized advice.',
        '',
        'Deal Velocity and its affiliates are not responsible for any decisions made or actions taken based on this tool. Any reliance you place on this information is strictly at your own risk. In no event will we be liable for any loss or damage, including indirect or consequential loss, arising from the use of this recommendation tool.'
      ];

      disclaimerText.forEach(paragraph => {
        if (paragraph === '') {
          yPos += 2;
        } else {
          const lines = pdf.splitTextToSize(paragraph, contentWidth);
          checkPageBreak(lines.length * 3 + 2);
          pdf.text(lines, margin, yPos);
          yPos += lines.length * 3 + 1;
        }
      });

      // Footer on last page
      pdf.setFontSize(8);
      pdf.setTextColor(100);
      pdf.text('Deal Velocity - https://dealvelocityfunnel.web.app', pageWidth / 2, pdf.internal.pageSize.getHeight() - 10, { align: 'center' });

      // Save PDF
      pdf.save(`Business-Structure-Recommendation-${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Filter questions based on previous answers
  const getVisibleQuestions = (): Question[] => {
    return questions.filter(q => !q.showIf || q.showIf(answers));
  };

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));

    const visibleQuestions = getVisibleQuestions();
    const currentQuestion = visibleQuestions[currentStep];

    if (currentQuestion.id === questionId) {
      const nextQuestions = questions.filter(q =>
        (!q.showIf || q.showIf({ ...answers, [questionId]: value }))
      );

      if (currentStep >= nextQuestions.length - 1) {
        setShowResult(true);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (showResult) {
      setShowResult(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResult(false);
  };

  const handleGetQuickStartKit = () => {
    navigate('/portal/products/quick-start-kit');
  };

  const handleViewProduct = (productId: string) => {
    navigate(`/portal/products/${productId}`);
  };

  const visibleQuestions = getVisibleQuestions();
  const currentQuestion = visibleQuestions[currentStep];
  const progress = ((currentStep + 1) / visibleQuestions.length) * 100;

  // Results view
  if (showResult) {
    const { recommendations, reasoning } = getRecommendations();
    const primaryId = recommendations[0];
    const primary = entities[primaryId];
    const alternatives = recommendations.slice(1).map(id => entities[id]);
    const PrimaryIcon = primary.icon;

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Your Recommendations</h1>
              <div className="flex gap-3">
                <button
                  onClick={generatePDF}
                  disabled={isGeneratingPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  {isGeneratingPDF ? 'Generating...' : 'Download PDF Report'}
                </button>
                <button
                  onClick={handleRestart}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Start Over
                </button>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Lightbulb className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
              <p className="text-gray-700">
                Based on your answers, we've identified <strong>{recommendations.length} suitable options</strong>.
                Review each carefully as your specific circumstances will determine the best fit.
              </p>
            </div>
          </div>

          {/* Key Decision Factors */}
          {reasoning.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
                Key Factors in Your Situation
              </h2>
              <div className="space-y-3">
                {reasoning.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                    <p className="font-semibold text-gray-900 mb-1">{item.factor}</p>
                    <p className="text-gray-700 text-sm">{item.recommendation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Primary Recommendation */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6 border-2 border-blue-400">
            <div className="flex items-start gap-4 mb-6">
              <div className={`${primary.color} p-4 rounded-lg flex-shrink-0`}>
                <PrimaryIcon className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h2 className="text-3xl font-bold text-gray-900">{primary.name}</h2>
                  <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                    Primary Option
                  </span>
                </div>
                <p className="text-lg text-gray-600">{primary.description}</p>
              </div>
            </div>

            {/* Conditional Guidance */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg mb-6 border border-blue-200">
              <p className="text-gray-900 font-medium mb-3">
                📊 <strong>When {primary.name} is the right choice:</strong>
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold mt-0.5">•</span>
                  <span>
                    If <strong>liability protection</strong> and <strong>professional credibility</strong> are priorities
                    {primaryId === 'pty-ltd' && ', Pty Ltd is your best option.'}
                    {primaryId === 'sole-proprietor' && ', but you want to minimize costs and complexity, Sole Proprietor works well.'}
                    {primaryId === 'partnership' && ', but you want flexibility and don\'t mind joint liability, Partnership could work.'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold mt-0.5">•</span>
                  <span>
                    If you plan to <strong>raise investment</strong> or <strong>scale significantly</strong>
                    {primaryId === 'pty-ltd' && ', Pty Ltd is essential.'}
                    {primaryId === 'sole-proprietor' && ', you should strongly consider Pty Ltd instead.'}
                    {primaryId === 'partnership' && ', consider converting to Pty Ltd before seeking investment.'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold mt-0.5">•</span>
                  <span>
                    If <strong>simplicity</strong> and <strong>low administrative burden</strong> matter most
                    {primaryId === 'sole-proprietor' && ', Sole Proprietor is ideal.'}
                    {primaryId === 'pty-ltd' && ', you may prefer Sole Proprietor initially despite the benefits above.'}
                    {primaryId === 'partnership' && ', both Partnership and Sole Proprietor offer simpler setups.'}
                  </span>
                </li>
              </ul>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Setup Cost</p>
                <p className="text-xl font-bold text-gray-900">{primary.setupCost}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Setup Time</p>
                <p className="text-xl font-bold text-gray-900">{primary.setupTime}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Annual Costs</p>
                <p className="text-xl font-bold text-gray-900">{primary.annualCosts}</p>
              </div>
            </div>

            {/* Pros and Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Advantages
                </h3>
                <ul className="space-y-2">
                  {primary.pros.map((pro, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <span className="text-green-500 mt-1">✓</span>
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Considerations
                </h3>
                <ul className="space-y-2">
                  {primary.cons.map((con, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <span className="text-orange-500 mt-1">!</span>
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Best For */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200 mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-1">✓ Best suited for:</p>
              <p className="text-gray-900">{primary.bestFor}</p>
            </div>

            {/* Required Documents */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-blue-500" />
                Required Documents & Agreements
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                You'll need to have these documents in place to operate legally as a {primary.name}:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                {primary.requiredDocuments.map((doc, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1 text-sm">□</span>
                    <span className="text-sm text-gray-700">{doc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Product Recommendations */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
                Recommended Deal Velocity Products
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Get these documents and templates ready-made to fast-track your {primary.name} setup:
              </p>
              <div className="space-y-4">
                {primary.recommendedProducts.map((product) => (
                  <div
                    key={product.productId}
                    className={`border-l-4 ${
                      product.priority === 'essential' ? 'border-red-500 bg-red-50' :
                      product.priority === 'recommended' ? 'border-blue-500 bg-blue-50' :
                      'border-gray-400 bg-gray-50'
                    } p-4 rounded-r-lg`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-bold text-gray-900">{product.name}</h4>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            product.priority === 'essential' ? 'bg-red-100 text-red-700' :
                            product.priority === 'recommended' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-200 text-gray-700'
                          }`}>
                            {product.priority.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{product.why}</p>
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-green-600">{product.price}</span>
                          <button
                            onClick={() => handleViewProduct(product.productId)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
                          >
                            View Details →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Alternative Options */}
          {alternatives.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Scale className="w-6 h-6 text-gray-600" />
                Alternative Options to Consider
              </h3>
              <p className="text-gray-600 mb-4 text-sm">
                These alternatives may be more suitable depending on your specific priorities and circumstances.
              </p>
              <div className="space-y-4">
                {alternatives.map((alt, altIdx) => {
                  const AltIcon = alt.icon;
                  const altId = recommendations[altIdx + 1];
                  return (
                    <div key={alt.name} className="border-2 border-gray-200 rounded-lg p-5 hover:border-blue-300 transition-colors">
                      <div className="flex items-start gap-4 mb-3">
                        <div className={`${alt.color} p-3 rounded-lg flex-shrink-0`}>
                          <AltIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1 text-lg">{alt.name}</h4>
                          <p className="text-sm text-gray-600 mb-3">{alt.description}</p>

                          {/* When to choose this alternative */}
                          <div className="bg-amber-50 p-3 rounded-lg mb-3 border border-amber-200">
                            <p className="text-sm text-gray-800">
                              <strong>Choose this if:</strong>
                              {altId === 'pty-ltd' && ' you prioritize growth potential, investor readiness, and liability protection over simplicity.'}
                              {altId === 'sole-proprietor' && ' you prioritize minimal costs and simplicity, and have low liability exposure.'}
                              {altId === 'partnership' && ' you want a collaborative structure with shared decision-making and are comfortable with joint liability.'}
                              {altId === 'trust' && ' your primary focus is asset protection and estate planning rather than active trading.'}
                              {altId === 'inc' && ' your profession requires specific incorporation under professional body rules.'}
                              {altId === 'npc' && ' your mission is purely public benefit without any profit distribution to members.'}
                            </p>
                          </div>

                          <div className="grid grid-cols-3 gap-3 text-xs">
                            <div>
                              <span className="text-gray-500">Setup:</span>
                              <span className="font-semibold text-gray-900 ml-1">{alt.setupCost}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Time:</span>
                              <span className="font-semibold text-gray-900 ml-1">{alt.setupTime}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Annual:</span>
                              <span className="font-semibold text-gray-900 ml-1">{alt.annualCosts}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Important Disclaimer */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Important Legal Disclaimer</h3>
                <div className="text-sm text-gray-700 space-y-2">
                  <p>
                    This recommendation tool provides <strong>general information only</strong> and should not be considered
                    professional legal, tax, or financial advice. Business structure decisions are complex and depend on your
                    specific circumstances, which this tool cannot fully assess.
                  </p>
                  <p>
                    While we endeavor to keep information accurate and up-to-date, we make <strong>no representations or
                    warranties</strong> about the completeness, accuracy, or suitability of the recommendations for your
                    specific situation. Laws and regulations change frequently and their application varies based on
                    individual circumstances.
                  </p>
                  <p>
                    <strong>Before making any final decision</strong>, you should consult with qualified professional
                    advisors including an attorney, accountant, and/or tax specialist who can evaluate your specific
                    situation and provide personalized advice.
                  </p>
                  <p>
                    <strong>Deal Velocity and its affiliates</strong> are not responsible for any decisions made or
                    actions taken based on this tool. Any reliance you place on this information is strictly at your
                    own risk. In no event will we be liable for any loss or damage, including indirect or consequential
                    loss, arising from the use of this recommendation tool.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Ready to Get Started?</h3>
            <p className="mb-6 text-blue-100">
              Our Quick-Start Kit includes detailed setup guides, registration checklists, and document templates
              for {primary.name} and other entity types, plus expert guidance on CIPC registration and SARS compliance.
            </p>
            <button
              onClick={handleGetQuickStartKit}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Get the Quick-Start Kit (R97)
            </button>
            <p className="mt-4 text-sm text-blue-200">
              Includes templates for all entity types • CIPC registration guides • Tax compliance checklists
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Question view (unchanged from before)
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl font-bold text-gray-900">Business Structure Wizard</h1>
            <span className="text-sm text-gray-600">
              Question {currentStep + 1} of {visibleQuestions.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {currentQuestion.question}
          </h2>
          <p className="text-gray-600 mb-8">{currentQuestion.description}</p>

          {/* Options */}
          <div className="space-y-4">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQuestion.id, option.value)}
                className={`w-full p-6 rounded-lg border-2 text-left transition-all hover:border-blue-400 hover:shadow-md ${
                  answers[currentQuestion.id] === option.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{option.icon}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{option.label}</p>
                  </div>
                  {answers[currentQuestion.id] === option.value && (
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-6 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleRestart}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>

        {/* Help text */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Need help deciding? Our Quick-Start Kit includes detailed guides for each entity type.
        </div>
      </div>
    </div>
  );
};

export default BusinessStructureWizard;
